import { C } from '@ridgewill/brand';
import type { Ups } from '../lib/fleet-api.js';
import { durationText, share } from '../lib/fleet-boxes.js';
import { outageLength, pct, shutdownLine, upsStateView, volts, watts } from '../lib/fleet-ups.js';
import { numeric } from '../lib/format.js';
import { DotPill, When } from '../components/ui.js';

/**
 * The UPS behind the CI boxes. RidgeWill/fleet-api#59.
 *
 * One card per UPS fleet-api knows, which today is the CyberPower whose USB
 * cable is in headless-2. Fed by props from the boxes answer CiFleet.tsx
 * already polls, so it costs no request of its own. The alerts for it are in
 * lib/fleet-alerts.ts; this only draws.
 */

/** Outages listed under the card. fleet-api sends up to ten. */
const OUTAGES_SHOWN = 3;

export function UpsCards({ ups }: { ups: Ups[] }) {
  return (
    <div style={s.grid}>
      {ups.map((u) => (
        <UpsCard key={`${u.box}:${u.name}`} ups={u} />
      ))}
    </div>
  );
}

function UpsCard({ ups }: { ups: Ups }) {
  const state = upsStateView(ups);
  const l = ups.last;
  const current = ups.state === 'reporting';
  const charge = share(l.charge_pct, 100);
  const line = shutdownLine(ups);
  const outages = ups.outages.slice(0, OUTAGES_SHOWN);
  const chargeColor = l.on_battery || l.low_battery ? (l.low_battery ? C.dangerText : C.warnDot) : C.successDot;

  return (
    <article style={s.card} aria-label={`UPS ${ups.name}, ${state.label}`}>
      <header style={s.head}>
        <span style={s.name}>{ups.model ?? ups.name}</span>
        <DotPill tone={state.tone}>{state.label}</DotPill>
      </header>
      <p style={s.sub}>
        {current ? 'Read' : 'Last read'} <When iso={ups.last_report_at} /> from {ups.box}
      </p>

      <dl style={{ ...s.metrics, opacity: current ? 1 : 0.6 }}>
        <div style={s.wide}>
          <dt style={s.label}>Charge</dt>
          <dd style={s.value}>
            <span style={numeric}>{pct(l.charge_pct)}</span>
            {charge !== null ? (
              <span role="meter" aria-label="Battery charge" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(charge * 100)} style={s.bar}>
                <span style={{ ...s.barFill, width: `${Math.round(charge * 100)}%`, background: chargeColor }} />
              </span>
            ) : null}
          </dd>
        </div>
        <Metric label="Runtime left" value={l.runtime_s === null ? 'Not reported' : durationText(l.runtime_s)} />
        <Metric
          label="Load"
          value={watts(l.watts)}
          note={l.load_pct !== null && l.nominal_w !== null ? `${Math.round(l.load_pct)}% of ${l.nominal_w} W` : undefined}
        />
        <Metric label="Input" value={volts(l.input_v)} />
        <Metric label="Shuts down at" value={line ?? 'Not reported'} />
      </dl>

      <section style={s.outages} aria-label={`Outages on ${ups.name}`}>
        <span style={s.label}>{outages.length > 1 ? 'Recent outages' : 'Last outage'}</span>
        {outages.length === 0 ? (
          <span style={s.sub}>None recorded</span>
        ) : (
          <ul style={s.list}>
            {outages.map((o) => (
              <li key={o.id} style={s.outage}>
                <span>
                  <When iso={o.started_at} /> · {outageLength(o, ups)}
                </span>
                <span style={s.sub}>
                  {o.min_charge_pct !== null ? `Lowest ${pct(o.min_charge_pct)}` : 'Lowest charge not reported'}
                  {o.low_battery ? ', reached the shutdown line' : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </article>
  );
}

function Metric({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <dt style={s.label}>{label}</dt>
      <dd style={s.value}>
        <span style={numeric}>{value}</span>
        {note ? <span style={{ color: C.muted, fontSize: '.75rem' }}> {note}</span> : null}
      </dd>
    </div>
  );
}

const s = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 290px), 1fr))', gap: '.75rem', padding: '1rem 1.1rem' },
  card: { display: 'grid', gap: '.6rem', alignContent: 'start', padding: '.85rem .95rem', background: C.page, border: `1px solid ${C.border}`, borderRadius: 10, minWidth: 0 },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' as const },
  name: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 700, fontSize: '.92rem' },
  sub: { margin: 0, fontSize: '.78rem', color: C.muted },
  metrics: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '.45rem .9rem', margin: 0 },
  wide: { gridColumn: '1 / -1', minWidth: 0 },
  label: { fontSize: '.7rem', fontWeight: 600, color: C.muted, textTransform: 'uppercase' as const, letterSpacing: '.04em' },
  value: { margin: 0, fontSize: '.85rem', display: 'grid', gap: '.25rem' },
  bar: { display: 'block', height: 6, borderRadius: 3, background: C.surface, border: `1px solid ${C.border}`, overflow: 'hidden' },
  barFill: { display: 'block', height: '100%' },
  outages: { display: 'grid', gap: '.35rem', padding: '.55rem 0 0', borderTop: `1px solid ${C.border}`, minWidth: 0 },
  list: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '.4rem' },
  outage: { display: 'grid', gap: '.1rem', fontSize: '.8rem' },
};
